import React, { useEffect, createElement } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Building2, ShieldCheck, Clock } from 'lucide-react';
import { QuoteForm } from '../components/QuoteForm';
import { useScreenInit } from '../useScreenInit';
const towns = ['Aldridge', 'Alcester', 'Atherstone', 'Bedworth', 'Bilston', 'Biddulph', 'Birmingham', 'Blackheath', 'Bloxwich', 'Bridgtown', 'Brierley Hill', 'Brownhills', 'Burntwood', 'Burton upon Trent', 'Cannock', 'Cheadle', 'Coleshill', 'Coventry', 'Cradley Heath', 'Dudley', 'Halesowen', 'Henley in Arden', 'Kidsgrove', 'Kingswinford', 'Kenilworth', 'Leek', 'Lichfield', 'Newcastle under Lyme', 'Nuneaton', 'Oldbury', 'Penkridge', 'Rugeley', 'Rugby', 'Rowley Regis', 'Royal Leamington Spa', 'Sedgley', 'Shipston on Stour', 'Smethwick', 'Solihull', 'Southam', 'Stafford', 'Stone', 'Stratford upon Avon', 'Stoke on Trent', 'Stourbridge', 'Studley', 'Sutton Coldfield', 'Tamworth', 'Tipton', 'Uttoxeter', 'Walsall', 'Warwick', 'Wednesbury', 'Wednesfield', 'Wellesbourne', 'West Bromwich', 'Willenhall', 'Wolverhampton'];
export function AreasWeCover() {
  const screenInit = useScreenInit();
  useEffect(() => {
    document.title = 'Areas we cover | Commercial air conditioning | AccuTemp';
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = 'AccuTemp provides commercial air conditioning installation, maintenance and TM44 compliance across the West Midlands and neighbouring counties. View our full service area list.';
  }, []);
  return <main className="min-h-screen bg-white pt-28" data-id="element-734">
      {/* Hero section */}
      <section className="bg-[#E8F0F8] py-20" data-id="element-735">
        <div className="container mx-auto px-6" data-id="element-736">
          <div className="max-w-4xl" data-id="element-737">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-738">
              <div className="flex items-center gap-2 text-accent font-semibold tracking-wider uppercase text-sm mb-4" data-id="element-739">
                <MapPin className="w-4 h-4" data-id="element-740" /> Service coverage
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-primary-dark mb-8" data-id="element-741">
                Areas we cover
              </h1>
              <p className="text-xl text-primary-dark/70 leading-relaxed max-w-3xl" data-id="element-742">
                AccuTemp is based in Coventry and serves commercial clients
                across the West Midlands and neighbouring counties. We
                specialise in commercial air conditioning installation,
                maintenance and TM44 compliance, delivering manufacturer-level
                support to businesses, landlords, and FM firms.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Locations list */}
      <section className="py-24" data-id="element-743">
        <div className="container mx-auto px-6" data-id="element-744">
          <div className="mb-16" data-id="element-745">
            <h2 className="text-2xl md:text-3xl font-serif text-primary-dark mb-4" data-id="element-746">
              Our service locations
            </h2>
            <p className="text-primary-dark/60" data-id="element-747">
              We provide rapid response and planned maintenance to commercial
              sites in the following areas:
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-4" data-id="element-748">
            {towns.map(town => <div key={town} className="flex items-center gap-3 py-2 border-b border-primary-dark/5 group hover:border-accent/30 transition-colors" data-id="element-749">
                <div className="w-1.5 h-1.5 rounded-full bg-accent/40 group-hover:bg-accent transition-colors" data-id="element-750"></div>
                <Link to={`/locations/${town.toLowerCase().replace(/\s+/g, '-')}`} className="text-primary-dark/80 hover:text-accent transition-colors font-medium" data-id="element-751">
                  {town}
                </Link>
              </div>)}
          </div>
        </div>
      </section>

      {/* Commercial only CTA */}
      <section className="py-24 bg-primary-dark text-white" data-id="element-752">
        <div className="container mx-auto px-6 lg:px-12" data-id="element-753">
          <div className="max-w-7xl mx-auto" data-id="element-754">
            <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start" data-id="element-755">
              <div className="pt-4" data-id="element-756">
                <h2 className="text-4xl md:text-5xl font-serif mb-10 leading-tight" data-id="element-757">
                  Commercial HVAC specialists
                </h2>
                <div className="space-y-10" data-id="element-758">
                  <div className="flex gap-6" data-id="element-759">
                    <div className="w-14 h-14 bg-white/5 rounded-2xl flex items-center justify-center shrink-0 border border-white/10" data-id="element-760">
                      <Building2 className="w-7 h-7 text-accent" data-id="element-761" />
                    </div>
                    <div data-id="element-762">
                      <h3 className="font-bold text-xl mb-3" data-id="element-763">
                        B2B and commercial only
                      </h3>
                      <p className="text-white/50 leading-relaxed" data-id="element-764">
                        We focus exclusively on commercial environments—offices,
                        retail, industrial, and healthcare. We do not undertake
                        residential work.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-6" data-id="element-765">
                    <div className="w-14 h-14 bg-white/5 rounded-2xl flex items-center justify-center shrink-0 border border-white/10" data-id="element-766">
                      <ShieldCheck className="w-7 h-7 text-accent" data-id="element-767" />
                    </div>
                    <div data-id="element-768">
                      <h3 className="font-bold text-xl mb-3" data-id="element-769">
                        Fully accredited
                      </h3>
                      <p className="text-white/50 leading-relaxed" data-id="element-770">
                        Constructionline Gold, REFCOM, and Safe Contractor
                        accredited for your peace of mind.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-6" data-id="element-771">
                    <div className="w-14 h-14 bg-white/5 rounded-2xl flex items-center justify-center shrink-0 border border-white/10" data-id="element-772">
                      <Clock className="w-7 h-7 text-accent" data-id="element-773" />
                    </div>
                    <div data-id="element-774">
                      <h3 className="font-bold text-xl mb-3" data-id="element-775">Rapid response</h3>
                      <p className="text-white/50 leading-relaxed" data-id="element-776">
                        Based in the heart of the West Midlands for fast
                        attendance to breakdowns and site surveys.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-[2.5rem] p-10 md:p-14 shadow-2xl" data-id="element-777">
                <div className="mb-10" data-id="element-778">
                  <h3 className="text-3xl font-serif text-primary-dark mb-4" data-id="element-779">
                    Request a free quote
                  </h3>
                  <p className="text-primary-dark/50 leading-relaxed" data-id="element-780">
                    Our design engineers are ready to help. Please note we only
                    accept commercial and B2B enquiries.
                  </p>
                </div>
                <QuoteForm data-id="element-781" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}